import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { CAMPAIGNS_PER_PAGE, CampaignsContext } from "contexts/Campaigns";
import { DiscoverTabView, DiscoverViewContext } from "contexts/DiscoverView";
import { NewCampaignProvider } from "contexts/NewCampaign";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import {
  Campaign,
  CampaignStats,
  CampaignStatus,
  UpgradeDialogSource,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import { PAGE_VISITED } from "constants/trackingProps";
import { isAssistantAvailable } from "features/Home/Sidebar/AssistantPreview";
import { displayLockedCampaignWall, formatDate } from "utils/time";
import { trackEvent } from "utils/tracking";

import CampaignCard from "./CampaignCard";
import { CampaignStatusChip } from "./CampaignStatusChip";
import NewCampaign from "./NewCampaign";
import NoCampaignsYet from "./NoCampaignYet";
import CampaignPreviewPage from "./PreviewPage";
import ResumeCampaignDialog from "./ResumeCampaignDialog";

const PAUSE_DIALOG_BODY =
  "Pausing cancels all unsent outreach emails in this campaign. " +
  "Follow-ups for outreach that's already been sent will still go out. " +
  "When you resume, you'll pick a new start date and we'll reschedule the remaining brands.";

const HEADER_BG = "#f9fbfc";

const headerCellSx = {
  backgroundColor: HEADER_BG,
  fontWeight: 600,
  fontSize: 12,
  color: grey[600],
  textTransform: "uppercase" as const,
  letterSpacing: 0.5,
  borderBottom: `1px solid ${grey[200]}`,
  py: 1.5,
};

interface CampaignRowProps {
  campaign: Campaign;
  stats?: CampaignStats;
  statsLoading: boolean;
  selected: boolean;
  onToggle: (id: number) => void;
  onDelete: (campaign: Campaign) => void;
  onPause: (campaign: Campaign) => void;
  onResume: (campaign: Campaign) => void;
  onRowClick: (campaign: Campaign) => void;
  onOpenAssistant: (campaign: Campaign) => void;
}

function CampaignRow({
  campaign,
  stats,
  statsLoading,
  selected,
  onToggle,
  onDelete,
  onPause,
  onResume,
  onRowClick,
  onOpenAssistant,
}: CampaignRowProps) {
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);
  const assistantAvailable = isAssistantAvailable();

  const dash = "—";
  const opensPct = ((stats?.openedPercentage || 0) * 100).toFixed(1);
  const respondedPct = ((stats?.respondedPercentage || 0) * 100).toFixed(1);

  return (
    <TableRow
      hover
      selected={selected}
      onClick={() => onRowClick(campaign)}
      sx={{
        cursor: "pointer",
        "&.Mui-selected": { backgroundColor: "rgba(99,102,241,0.04)" },
        "&.Mui-selected:hover": { backgroundColor: "rgba(99,102,241,0.07)" },
      }}
    >
      {/* Checkbox */}
      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          size="small"
          checked={selected}
          onChange={() => onToggle(campaign.id!)}
        />
      </TableCell>

      {/* Campaign Name */}
      <TableCell
        sx={{ textDecoration: "none", color: "inherit", display: "table-cell" }}
      >
        <Typography sx={{ fontSize: 14 }}>{campaign.name}</Typography>
      </TableCell>

      {/* Status */}
      <TableCell sx={{ display: "table-cell" }}>
        {campaign?.status && campaign?.startDate ? (
          <CampaignStatusChip
            status={campaign.status}
            startDate={campaign.startDate}
          />
        ) : (
          <Typography sx={{ fontSize: 13, color: grey[400] }}>—</Typography>
        )}
      </TableCell>

      {/* Date Range */}
      <TableCell
        sx={{ textDecoration: "none", color: "inherit", display: "table-cell" }}
      >
        {campaign?.startDate || campaign?.endDate ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box
                component="i"
                className="fa-regular fa-calendar"
                sx={{ fontSize: 11, color: grey[400] }}
              />
              <Typography sx={{ fontSize: 13, whiteSpace: "nowrap" }}>
                {formatDate(campaign?.startDate)}
                {campaign?.endDate && (
                  <> &ndash; {formatDate(campaign.endDate)}</>
                )}
              </Typography>
            </Box>
            {(campaign?.timezone || campaign?.emailsPerDay) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "wrap",
                }}
              >
                {campaign?.timezone && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      bgcolor: grey[100],
                      border: "1px solid",
                      borderColor: grey[300],
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.25,
                      fontSize: 11,
                      color: grey[700],
                      fontWeight: 500,
                    }}
                  >
                    <Box
                      component="i"
                      className="fa-regular fa-clock"
                      sx={{ fontSize: 10 }}
                    />
                    {campaign.timezone}
                  </Box>
                )}
                {campaign?.emailsPerDay && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      bgcolor: "purple.light",
                      border: "1px solid",
                      borderColor: "purple.main",
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.25,
                      fontSize: 11,
                      color: "purple.dark",
                      fontWeight: 600,
                    }}
                  >
                    <Box
                      component="i"
                      className="fa-regular fa-envelope"
                      sx={{ fontSize: 10 }}
                    />
                    {campaign.emailsPerDay} emails/day
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: grey[400] }}>
            {dash}
          </Typography>
        )}
      </TableCell>

      {/* Brands */}
      <TableCell
        align="center"
        sx={{ textDecoration: "none", color: "inherit", display: "table-cell" }}
      >
        <Typography sx={{ fontSize: 14 }}>
          {campaign.campaignBrandCount ?? dash}
        </Typography>
      </TableCell>

      {/* Sent */}
      <TableCell
        align="center"
        sx={{ textDecoration: "none", color: "inherit", display: "table-cell" }}
      >
        <Typography sx={{ fontSize: 14 }}>
          {statsLoading ? dash : stats?.outreachCount ?? dash}
        </Typography>
      </TableCell>

      {/* Opens */}
      <TableCell
        align="center"
        sx={{ textDecoration: "none", color: "inherit", display: "table-cell" }}
      >
        {!subscription?.isSubscribed ? (
          <MuiLink
            onClick={(e) => {
              e.preventDefault();
              setUpgradeDialogSource(UpgradeDialogSource.email_tracking);
            }}
            sx={{ fontSize: 12 }}
          >
            Upgrade
          </MuiLink>
        ) : (
          <Typography sx={{ fontSize: 14 }}>
            {statsLoading ? dash : `${stats?.openedCount || 0} (${opensPct}%)`}
          </Typography>
        )}
      </TableCell>

      {/* Responses */}
      <TableCell
        align="center"
        sx={{ textDecoration: "none", color: "inherit", display: "table-cell" }}
      >
        <Typography sx={{ fontSize: 14 }}>
          {statsLoading
            ? dash
            : `${stats?.respondedCount || 0} (${respondedPct}%)`}
        </Typography>
      </TableCell>

      {/* Action */}
      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
          }}
        >
          {assistantAvailable && (
            <Tooltip title="Start AI chat about this campaign" placement="top">
              <Button
                size="small"
                variant="outlined"
                disableElevation
                onClick={() => onOpenAssistant(campaign)}
                startIcon={
                  <Box
                    component="i"
                    className="fa-solid fa-sparkles"
                    sx={{ fontSize: 12, color: "#00875a" }}
                  />
                }
                sx={{
                  textTransform: "none",
                  minWidth: 0,
                  px: 1.25,
                  py: 0.25,
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: 999,
                  color: "#00875a",
                  backgroundColor: "#dcfce7",
                  borderColor: "#a7f3d0",
                  "&:hover": {
                    backgroundColor: "#bbf7d0",
                    borderColor: "#86efac",
                  },
                }}
              >
                AI
              </Button>
            </Tooltip>
          )}
          {campaign.status === CampaignStatus.paused ? (
            <Tooltip title="Resume campaign" placement="top">
              <IconButton
                size="small"
                onClick={() => onResume(campaign)}
                sx={{ color: grey[400], "&:hover": { color: "#00875a" } }}
              >
                <Box
                  component="i"
                  className="fa-solid fa-play"
                  sx={{ fontSize: 13 }}
                />
              </IconButton>
            </Tooltip>
          ) : campaign.status === CampaignStatus.in_progress ? (
            <Tooltip
              title="Pause — cancels unsent outreach emails"
              placement="top"
            >
              <IconButton
                size="small"
                onClick={() => onPause(campaign)}
                sx={{ color: grey[400], "&:hover": { color: "warning.main" } }}
              >
                <Box
                  component="i"
                  className="fa-solid fa-pause"
                  sx={{ fontSize: 13 }}
                />
              </IconButton>
            </Tooltip>
          ) : null}
          <IconButton
            size="small"
            onClick={() => onDelete(campaign)}
            sx={{ color: grey[400], "&:hover": { color: "error.main" } }}
          >
            <Box
              component="i"
              className="fa-regular fa-trash"
              sx={{ fontSize: 14 }}
            />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
}

export default function AllCampaigns() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setCompleteOnboardingDialogOpen, incompleteProfile, noTemplates } =
    useContext(OnboardingDialogsContext);
  const { discoverTab, setDiscoverTab, openAssistant } =
    useContext(DiscoverViewContext);
  const _isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("xl"));
  const isMobileScreen =
    _isMobileScreen || (isMediumScreen && discoverTab !== null);

  const incompleteOnboarding = incompleteProfile || noTemplates;

  const {
    campaigns,
    getCampaigns,
    campaignsLoading,
    campaignsCount,
    campaignPage,
    campaignStatsLoading,
    campaignStats,
    deleteCampaign,
    pauseCampaign,
    resumeCampaign,
  } = useContext(CampaignsContext);
  const { setAlert } = useContext(AlertContext);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [singleDeleteTarget, setSingleDeleteTarget] = useState<Campaign | null>(
    null,
  );
  const [pauseTarget, setPauseTarget] = useState<Campaign | null>(null);
  const [resumeTarget, setResumeTarget] = useState<Campaign | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (campaignsCount === null && !campaignsLoading) getCampaigns(1);
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const openUploadBrands = () => {
    trackEvent("Upload Brands Button Clicked");
    setDiscoverTab(DiscoverTabView.UPLOAD_BRANDS);
  };

  const createCampaignClicked = () => {
    if (incompleteOnboarding && !currentOrg?.isBrand) {
      trackEvent("New Campaign Button Clicked - Complete Onboarding Modal");
      setCompleteOnboardingDialogOpen(true);
      return;
    }
    navigate(`/${routes.campaignView}/new`);
    trackEvent("New Campaign Button Clicked");
  };

  const toggleRow = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allSelected =
    campaigns.length > 0 && campaigns.every((c) => selected.has(c.id!));
  const someSelected = campaigns.some((c) => selected.has(c.id!));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(campaigns.map((c) => c.id!)));
    }
  };

  const handleBulkDelete = async () => {
    setDeleteLoading(true);
    await deleteCampaign(Array.from(selected));
    setSelected(new Set());
    setBulkDeleteOpen(false);
    setDeleteLoading(false);
  };

  const handleSingleDelete = async () => {
    if (!singleDeleteTarget?.id) return;
    setDeleteLoading(true);
    await deleteCampaign([singleDeleteTarget.id]);
    setSingleDeleteTarget(null);
    setDeleteLoading(false);
  };

  const handlePauseConfirm = async () => {
    if (!pauseTarget?.id) return;
    setActionLoading(true);
    const ok = await pauseCampaign(pauseTarget.id);
    if (ok) setAlert("Campaign paused", "success");
    setPauseTarget(null);
    setActionLoading(false);
  };

  const handleResumeConfirm = async (startDate: string) => {
    if (!resumeTarget?.id) return;
    setActionLoading(true);
    const ok = await resumeCampaign(resumeTarget.id, startDate);
    if (ok) setAlert("Campaign resumed", "success");
    setResumeTarget(null);
    setActionLoading(false);
  };

  const handleOpenAssistantForCampaign = (campaign: Campaign) => {
    if (!campaign.id) return;
    openAssistant({
      promptPage: "campaigns",
      entity: { entityType: "campaign", entityId: campaign.id },
      source: "Campaign Card CTA",
    });
  };

  if (!currentOrg?.isPaidUser && displayLockedCampaignWall()) {
    return <CampaignPreviewPage />;
  }

  return (
    <Box sx={{ my: 2, mx: { xs: 1, sm: 2 } }}>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <Grid item xs="auto">
          <Typography variant="h3" sx={{ fontWeight: 700 }}>
            My Campaigns
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#4A5565", mt: 0.5 }}>
            Organize your brand outreach into targeted campaigns
          </Typography>
        </Grid>
        <Grid
          item
          xs="auto"
          sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
        >
          {someSelected && (
            <StyledLargeButton
              onClick={() => setBulkDeleteOpen(true)}
              color="secondary"
            >
              <Box
                component="i"
                className="fa-regular fa-trash"
                sx={{ mr: 0.75, fontSize: 13 }}
              />
              Delete {selected.size} campaign{selected.size > 1 ? "s" : ""}
            </StyledLargeButton>
          )}
          {!isMobileScreen && (
            <Button
              size="small"
              variant="outlined"
              disableElevation
              sx={{
                borderRadius: 9999,
                textTransform: "none",
                color: "#364153",
                borderColor: "#e5e7eb",
                fontSize: 12,
                fontWeight: 500,
                height: 30,
                px: 1.5,
                "&:hover": {
                  borderColor: "#d1d5db",
                  backgroundColor: "#f9fafb",
                },
              }}
              onClick={openUploadBrands}
            >
              Upload Brands{" "}
              <Box
                component="i"
                className="fa-regular fa-upload"
                sx={{ ml: 0.5 }}
              />
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={createCampaignClicked}
            sx={{
              borderRadius: 9999,
              textTransform: "none",
              backgroundColor: "#009966",
              color: "#fff",
              fontSize: 12,
              fontWeight: 500,
              height: 30,
              px: 1.5,
              "&:hover": {
                backgroundColor: "#00875a",
              },
            }}
          >
            <Box
              component="i"
              className="fa-solid fa-plus"
              sx={{ mr: 0.5, fontSize: 11 }}
            />
            Create Campaign
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {campaignsLoading || campaigns.length > 0 ? (
        <>
          {isMobileScreen ? (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                pb: 10,
              }}
            >
              {campaigns.map((campaign) => (
                <Box key={campaign.id}>
                  <CampaignCard
                    campaign={campaign}
                    stats={campaignStats[campaign.id!]}
                    statsLoading={campaignStatsLoading[campaign.id!]}
                    onDelete={setSingleDeleteTarget}
                    onPause={setPauseTarget}
                    onResume={setResumeTarget}
                    onOpenAssistant={handleOpenAssistantForCampaign}
                  />
                </Box>
              ))}
              {campaignsLoading && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Skeleton variant="rounded" width="100%" height={120} />
                  <Skeleton variant="rounded" width="100%" height={120} />
                  <Skeleton variant="rounded" width="100%" height={120} />
                </Box>
              )}
            </Box>
          ) : (
            <>
              <TableContainer sx={{ mt: 2 }}>
                <Table
                  sx={{
                    "& .MuiTableCell-root": {
                      borderBottom: `1px solid ${grey[100]}`,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={headerCellSx}>
                        <Checkbox
                          size="small"
                          checked={allSelected}
                          indeterminate={someSelected && !allSelected}
                          onChange={toggleAll}
                        />
                      </TableCell>
                      <TableCell sx={headerCellSx}>Campaign Name</TableCell>
                      <TableCell sx={headerCellSx}>Status</TableCell>
                      <TableCell sx={headerCellSx}>Date Range</TableCell>
                      <TableCell align="center" sx={headerCellSx}>
                        Brands
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx}>
                        Sent
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx}>
                        Opens
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx}>
                        Responses
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map((campaign) => (
                      <CampaignRow
                        key={campaign.id}
                        campaign={campaign}
                        stats={campaignStats[campaign.id!]}
                        statsLoading={campaignStatsLoading[campaign.id!]}
                        selected={selected.has(campaign.id!)}
                        onToggle={toggleRow}
                        onDelete={setSingleDeleteTarget}
                        onPause={setPauseTarget}
                        onResume={setResumeTarget}
                        onOpenAssistant={handleOpenAssistantForCampaign}
                        onRowClick={(c) => {
                          trackEvent("Campaign Row Clicked");
                          navigate(`campaign/${c.id}`);
                        }}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {campaignsLoading && (
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Skeleton variant="rounded" width="100%" height={56} />
                  <Skeleton variant="rounded" width="100%" height={56} />
                  <Skeleton variant="rounded" width="100%" height={56} />
                </Box>
              )}
            </>
          )}
          {campaignsCount !== null &&
            campaignsCount > CAMPAIGNS_PER_PAGE &&
            !campaignsLoading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  my: 3,
                  pb: 4,
                }}
              >
                <Pagination
                  count={Math.ceil(campaignsCount! / CAMPAIGNS_PER_PAGE)}
                  page={campaignPage}
                  onChange={(_e, page) => getCampaigns(page)}
                />
              </Box>
            )}
        </>
      ) : (
        <NoCampaignsYet onCreateCampaign={createCampaignClicked} />
      )}

      <ConfirmDialog
        header="Delete Campaigns"
        subheader={`Are you sure you want to delete ${selected.size} campaign${selected.size > 1 ? "s" : ""}? This action cannot be undone.`}
        open={bulkDeleteOpen}
        handleClose={() => setBulkDeleteOpen(false)}
        handleConfirm={handleBulkDelete}
        loading={deleteLoading}
      />

      <ConfirmDialog
        header="Delete Campaign"
        subheader={`Are you sure you want to delete "${singleDeleteTarget?.name}"? This action cannot be undone.`}
        open={Boolean(singleDeleteTarget)}
        handleClose={() => setSingleDeleteTarget(null)}
        handleConfirm={handleSingleDelete}
        loading={deleteLoading}
      />

      <ConfirmDialog
        header="Pause campaign"
        subheader={PAUSE_DIALOG_BODY}
        confirmText="Pause"
        open={Boolean(pauseTarget)}
        handleClose={() => setPauseTarget(null)}
        handleConfirm={handlePauseConfirm}
        loading={actionLoading}
      />

      <ResumeCampaignDialog
        campaign={resumeTarget}
        open={Boolean(resumeTarget)}
        loading={actionLoading}
        handleClose={() => setResumeTarget(null)}
        handleConfirm={handleResumeConfirm}
      />

      <Routes>
        <Route
          path="new/*"
          element={
            <NewCampaignProvider>
              <NewCampaign />
            </NewCampaignProvider>
          }
        />
        <Route
          path="campaign/:campaignId/*"
          element={
            <NewCampaignProvider>
              <NewCampaign />
            </NewCampaignProvider>
          }
        />
      </Routes>
    </Box>
  );
}
